import Config from '../../config';
import { FeedActions, getFeedActions } from './FeedState';
import _ from 'lodash';
import { SymbolChannels } from '../../types';
import { MarketNews } from '@tradingblock/types';

// NewsWebSocketService.ts
class NewsWebSocketService {
  private static instance: NewsWebSocketService;
  private ws: WebSocket | null = null;
  private newsSubscriptions: SymbolChannels[] = [];

  private constructor() {}

  // Singleton instance access
  public static getInstance(): NewsWebSocketService {
    if (!NewsWebSocketService.instance) {
      NewsWebSocketService.instance = new NewsWebSocketService();
      console.log('new websocket instance created');
    }
    return NewsWebSocketService.instance;
  }

  public updateNewsSubcriptions(newsSubscriptions: SymbolChannels[]) {
    this.newsSubscriptions = newsSubscriptions;
  }

  // Initialize WebSocket connection
  public async connect(dispatch: React.Dispatch<FeedActions>, dispatcher: any, apiToken: string) {
    try {
      if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
        this.ws = new WebSocket(Config.marketNewsWebsockets + '?token=' + apiToken);

        this.ws.onopen = () => {
          dispatcher.connected();
          getFeedActions(dispatch).setNewsStatus('open');
        };

        this.ws.onmessage = (ev: MessageEvent) => {
          const val = ev.data;
          const jsonVal: MarketNews = JSON.parse(val);
          dispatcher.receivedNews(jsonVal);
        };

        this.ws.onclose = (ev: CloseEvent) => {
          if (ev.reason) {
            getFeedActions(dispatch).setNewsCloseReason(ev.reason);
            console.warn(ev.reason);
          }
          getFeedActions(dispatch).setNewsStatus('closed');
        };

        this.ws.onerror = error => {
          dispatcher.errored({ error: error, reason: 'Unable to connect', message: 'Datafeed error' });
          getFeedActions(dispatch).setNewsStatus('error');
          console.error('News WebSocket error:', error);
        };
      }
    } catch (createWebsocketError) {
      dispatcher.errored({
        error: createWebsocketError,
        reason: 'Error creating news websocket',
        message: 'News Websocket error',
      });
    }
  }

  public manageNews = (data: string | ArrayBufferLike | Blob | ArrayBufferView) => {
    let parsedData: SymbolChannels[];
    let foundSymbols: SymbolChannels[] = [];
    let notFoundSymbols: SymbolChannels[] = [];
    let removeSymbols: SymbolChannels[] = [];

    if (typeof data === 'string') {
      parsedData = JSON.parse(data);
      parsedData.forEach(s => {
        if (this.newsSubscriptions.find(ns => ns.name === s.name)) {
          foundSymbols.push(s);
        } else {
          notFoundSymbols.push(s);
        }
      });
      this.newsSubscriptions.forEach(ns => {
        if (!parsedData.find(s => s.name === ns.name)) {
          removeSymbols.push(ns);
        }
      });

      //NOT FOUND SYMBOLS ARE ADDED
      let notFoundCombinedSymbols: SymbolChannels[] = [];
      if (notFoundSymbols && notFoundSymbols.length > 0) {
        const updatedSubscriptions = this.mergeArrays(this.newsSubscriptions, notFoundSymbols);
        this.newsSubscriptions = [...updatedSubscriptions];
        notFoundCombinedSymbols = [...notFoundSymbols];
      }

      //SUB
      if (notFoundCombinedSymbols && notFoundCombinedSymbols.length > 0) {
        let notFoundData: { type: string; symbol: string; newsChannels: string[] }[] = [];

        notFoundCombinedSymbols.forEach(s => {
          notFoundData = [...notFoundData, { type: 'Subscribe', symbol: s.name, newsChannels: s.channels }];
        });
        const cmd = JSON.stringify({ requests: notFoundData });

        if (this.ws) this.ws.send(cmd);
      }

      //REMOVE SYMBOLS ARE ADDED
      let removeCombinedSymbols: SymbolChannels[] = [];
      if (removeSymbols && removeSymbols.length > 0) {
        // getFeedActions(this.dispatch).removeNewsSubscriptions(removeSymbols.map(s => s.name));
        const updatedSubscriptions = this.newsSubscriptions.filter(
          s => !removeSymbols.map(s => s.name).includes(s.name)
        );
        this.newsSubscriptions = [...updatedSubscriptions];
        removeCombinedSymbols = [...removeSymbols];
      }

      // UNSUB
      if (removeCombinedSymbols && removeCombinedSymbols.length > 0) {
        let removeData: { type: string; symbol: string }[] = [];
        removeCombinedSymbols.forEach(s => {
          removeData = [...removeData, { type: 'UnSubscribe', symbol: s.name }];
        });
        const cmd = JSON.stringify({ requests: removeData });

        if (this.ws) this.ws.send(cmd);
      }

      //FOUND SYMBOLS ARE ADDED
      let foundCombinedSymbols: SymbolChannels[] = [];
      if (foundSymbols && foundSymbols.length > 0) {
        // getFeedActions(this.dispatch).adjustNewsSubscriptions(foundSymbols);
        const adjustedSubscriptions = this.newsSubscriptions.map(s => {
          const foundSymbol = foundSymbols.find(fs => fs.name === s.name);
          if (foundSymbol) return foundSymbol;
          else return s;
        });
        this.newsSubscriptions = [...adjustedSubscriptions];
        foundCombinedSymbols = [...foundSymbols];
      }

      // RESUBBED
      if (foundCombinedSymbols && foundCombinedSymbols.length > 0) {
        let foundData: { type: string; symbol: string; newsChannels: string[] }[] = [];
        foundCombinedSymbols.forEach(s => {
          foundData = [...foundData, { type: 'Subscribe', symbol: s.name, newsChannels: s.channels }];
        });
        const cmd = JSON.stringify({ requests: foundData });

        if (this.ws) this.ws.send(cmd);
      }
    }
  };

  public mergeArrays(arr1: SymbolChannels[], arr2: SymbolChannels[]) {
    const map = new Map();

    // Function to add items to the map
    const addToMap = (item: any) => {
      if (!map.has(item.name)) {
        map.set(item.name, { ...item });
      } else {
        map.get(item.name).channels = [...new Set([...map.get(item.name).channels, ...item.channels])];
      }
    };

    // Add items from both arrays to the map
    arr1.forEach(addToMap);
    arr2.forEach(addToMap);

    // Convert the map back to an array
    return Array.from(map.values());
  }

  // Send message to WebSocket
  public sendMessage(message: string) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.manageNews(message);
    } else {
      console.error('News WebSocket is not open');
    }
  }

  public shouldTryReconnect = () => {
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
      return true;
    }
    return false;
  };

  // Close WebSocket connection
  public closeConnection() {
    if (this.ws) {
      this.ws.close();
    }
  }
}

export const newsWebSocketService = NewsWebSocketService.getInstance();
